import { geometryKey } from "./gisLayer.ts";
import { nextId } from "./entityHelpers.ts";

export interface GisColumnMapping {
  /** .dbf/GeoJSON column with each element's name. */
  nameField: string;
}

/**
 * Shared shape behind `importAdditionalSubBasins` (subBasinEntities.ts),
 * `importAquifersFromGeo` (aquiferEntities.ts) and `importOutletPointsFromGeo`
 * (outletPointEntities.ts) — all three used to hand-write the same 4 steps
 * (drop features whose geometry already exists, work out the next free GIS
 * code, build the new entities, tag the GeoJSON with a link field for
 * `linkFn` to match them back up) with only the "how to build one new
 * entity" step actually differing per entity type. Found duplicated 3x by
 * the 2026-08-24 structure/maintainability review; merged here so a fix to
 * the shared parts (the dedup/numbering logic) only needs applying once.
 * `makeEntity` gets each feature's own resolved GIS code (already offset by
 * `firstGisCode`) and its 0-based index within the batch.
 */
export function importEntitiesFromGeo<T, TLayer>(
  geojson: GeoJSON.FeatureCollection,
  existingGeometries: ReadonlySet<string> | undefined,
  existingEntities: T[],
  gisCodeOf: (entity: T) => number | null,
  makeEntity: (feature: GeoJSON.Feature, gisCode: number, index: number) => T,
  linkFn: (geojsonWithLink: GeoJSON.FeatureCollection, entities: T[], linkField: string) => TLayer
): { newEntities: T[]; newLayer: TLayer } {
  const LINK_FIELD = "__codGis__";
  const newFeatures = existingGeometries
    ? geojson.features.filter(
        (f) => !existingGeometries.has(geometryKey(f.geometry as GeoJSON.Geometry))
      )
    : geojson.features;
  const firstGisCode = nextId(
    existingEntities.filter((e) => gisCodeOf(e) != null),
    (e) => gisCodeOf(e) as number
  );

  const newEntities = newFeatures.map((f, i) => makeEntity(f, firstGisCode + i, i));

  const geojsonWithLink: GeoJSON.FeatureCollection = {
    type: "FeatureCollection",
    features: newFeatures.map((f, i) => ({
      ...f,
      properties: { ...f.properties, [LINK_FIELD]: firstGisCode + i },
    })),
  };

  const allEntities = [...existingEntities, ...newEntities];
  return { newEntities, newLayer: linkFn(geojsonWithLink, allEntities, LINK_FIELD) };
}
